import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';

type PageViewTrackingPayload = {
  event: 'page_view';
  data: {
    page: string;
    referrer: string;
    medium: string;
    registration_step?: number;
  };
  session_token: string;
};

type SourceLandingTrackingPayload = {
  event: 'source_landing';
  data: {
    source_type: string;
    source_id: string;
    campaign: string;
    landing_page: string;
    medium: string;
  };
  timestamp: string;
  session_token: string;
};

const TRACKING_SESSION_KEY = 'tracking_session_token';

function getSessionToken(): string {
  const existingToken = sessionStorage.getItem(TRACKING_SESSION_KEY);
  if (existingToken) return existingToken;

  const generatedToken =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;

  sessionStorage.setItem(TRACKING_SESSION_KEY, generatedToken);
  return generatedToken;
}

function getReferrerPath(): string {
  if (!document.referrer) return '';

  try {
    const referrerUrl = new URL(document.referrer);
    return `${referrerUrl.pathname}${referrerUrl.search}`;
  } catch {
    return document.referrer;
  }
}

function formatTimestamp(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}:${pad(date.getSeconds())}`;
}

function RouteTracker() {
  const location = useLocation();
  const lastTrackedRef = useRef<string>('');

  const performTracking = (registrationStepOverride?: number) => {
    const existingSessionToken = sessionStorage.getItem(TRACKING_SESSION_KEY);
    const sessionToken = existingSessionToken || getSessionToken();
    const queryParams = new URLSearchParams(location.search);
    const sourceTypeParam = queryParams.get('source_type');
    const mediumParam = queryParams.get('medium') || queryParams.get('utm_medium');

    // Recupera lo step di registrazione se siamo sulla pagina di registration
    let registrationStep: number | undefined = registrationStepOverride;
    if (registrationStep === undefined && location.pathname.includes('register')) {
      try {
        const stored = localStorage.getItem('registrationForm');
        if (stored) {
          const parsed = JSON.parse(stored);
          registrationStep = parsed.currentStep ?? undefined;
        }
      } catch (error) {
        console.error('Errore nel recuperare registration step:', error);
      }
    }

    const pageViewPayload: PageViewTrackingPayload = {
      event: 'page_view',
      data: {
        page: location.pathname,
        referrer: getReferrerPath(),
        medium: queryParams.get('medium') || queryParams.get('utm_medium') || 'web',
        ...(registrationStep !== undefined && { registration_step: registrationStep }),
      },
      session_token: sessionToken,
    };

    const landingPayload: SourceLandingTrackingPayload | null =
      location.pathname === '/' && !existingSessionToken && sourceTypeParam && mediumParam
        ? {
            event: 'source_landing',
            data: {
              source_type: sourceTypeParam,
              source_id: queryParams.get('source_id') || '',
              campaign: queryParams.get('campaign') || '',
              landing_page: location.pathname,
              medium: mediumParam,
            },
            timestamp: formatTimestamp(new Date()),
            session_token: sessionToken,
          }
        : null;

    const trackRoute = async () => {
      try {
        await api.post('/track', pageViewPayload);
        if (landingPayload) {
          await api.post('/track', landingPayload);
        }
      } catch (error) {
        console.error('Errore tracking route:', error);
      }
    };

    void trackRoute();
  };

  useEffect(() => {
    const routeKey = `${location.pathname}${location.search}`;
    if (lastTrackedRef.current === routeKey) return;
    lastTrackedRef.current = routeKey;

    performTracking();
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleStepChange = (e: Event) => {
      const step = (e as CustomEvent).detail;
      performTracking(step);
    };
    window.addEventListener('registrationStepChange', handleStepChange);
    return () => window.removeEventListener('registrationStepChange', handleStepChange);
  }, [location.pathname]);

  return null;
}

export default RouteTracker;
